/**
 * 기기 식별 — M6 결정 구현 (스펙 §5-4).
 *
 * ── 원칙 ──────────────────────────────────────────────────
 *   기기 식별자는 발급받는 것이지 만들어내는 것이 아니다.
 *   못 받으면 만들지 말고, 못 받았다고 말한다.
 *
 * ── 왜 ────────────────────────────────────────────────────
 *   기존 Svelte 앱(apps/client/src/lib/device.ts)은 네이티브 ID 를 못 받으면
 *   crypto.randomUUID() 로 새 신원을 지어내 localStorage 에 박았다. 그 결과:
 *     · 가드 켜기 전 → 승인 없이 기기 이관 = 대리출석 갭
 *     · 가드 켠 후   → 차단 = PENDING = 태깅 403. 30일 711건 / 55명
 *   뿌리는 셋이었다.
 *     ① iOS `identifierForVendor?.uuidString ?? ""`  (IDFV nil → 빈 문자열)
 *     ② Android `Settings.Secure.getString(…) ?: ""` + catch → ""
 *     ③ 웹이 빈 문자열을 "네이티브 아님"으로 보고 UUID 를 생성   ← 진짜 결함
 *   ①②는 네이티브 릴리스가 필요하지만, ③은 여기서 끝낼 수 있다.
 */

const BROWSER_ID_KEY = 'biznomi_device_id'

/** 네이티브가 주입을 마칠 때까지 기다리는 시간 (Android 는 onPageFinished 보험 주입이 있다) */
const NATIVE_WAIT_MS = 1500
const POLL_INTERVAL_MS = 100

export type DeviceIdentity =
  /** 앱이 준 하드웨어 ID */
  | { source: 'native'; id: string; platform: 'ios' | 'android' }
  /** 순수 브라우저 — 기기에 종속되지 않는 약한 신원 */
  | { source: 'browser'; id: string }
  /** 앱 안인데 ID 를 못 받았다. 만들지 않는다. */
  | { source: 'unavailable'; reason: 'native-id-missing' | 'native-id-malformed' }

interface NativeGlobals {
  __NATIVE_DEVICE_ID__?: string
  __NATIVE_PLATFORM__?: string
  IS_NATIVE_APP?: boolean
}

function nat(): NativeGlobals {
  return (typeof window === 'undefined' ? {} : window) as NativeGlobals
}

/** WebView 셸 안인가 — 플랫폼 플래그가 있으면 앱이다 */
export function isNativeApp(): boolean {
  const w = nat()
  return Boolean(w.__NATIVE_PLATFORM__ || w.IS_NATIVE_APP)
}

/**
 * 명백히 신원이 아닌 값만 거른다.
 *
 * ⚠️ 처음엔 형식을 엄격히 검사했다(iOS 대문자 UUID / Android 16 hex).
 *    prod 로그 실측으로 기기ID 116개를 분류해보니 74개 네이티브 값이 전부 그 형식이었다.
 *    가정은 맞았다. 그런데 그게 엄격 검사의 근거가 되지는 않는다 —
 *
 *      너무 엄격 → 멀쩡한 기기가 잠긴다 (지금 고치고 있는 바로 그 사고)
 *      너무 느슨 → OS 가 준 값을 그대로 쓴다 (어차피 그게 그 기기의 신원이다)
 *
 *    위험이 완전히 비대칭이다. 117번째 기기가 다른 형식을 줄 가능성은 0 이 아니고,
 *    그때 그 사람은 출근을 못 찍는다. 우리가 막아야 하는 건 "지어내지 않는 것"이지
 *    "형식이 예쁜 것"이 아니다. 그래서 명백한 쓰레기만 거른다.
 */
const PLACEHOLDERS = new Set(['null', 'undefined', 'nan', 'none', 'unknown', '0', '-', 'false'])

function isUsableId(id: string): boolean {
  if (id.length < 8) return false
  if (PLACEHOLDERS.has(id.toLowerCase())) return false
  // 전부 같은 문자 (0000…, ----…) = 신원이 아니다
  if (/^(.)\1+$/.test(id)) return false
  return true
}

function readNative(): { id: string; platform: 'ios' | 'android' } | null {
  const w = nat()
  const id = (w.__NATIVE_DEVICE_ID__ ?? '').trim()
  if (!id) return null
  const platform = w.__NATIVE_PLATFORM__ === 'ios' ? 'ios' : 'android'
  return { id, platform }
}

/** 브라우저 전용 ID. **네이티브 앱 안에서는 절대 호출하지 않는다.** */
function browserId(): string {
  const saved = localStorage.getItem(BROWSER_ID_KEY)
  if (saved) return saved
  const fresh = crypto.randomUUID()
  localStorage.setItem(BROWSER_ID_KEY, fresh)
  return fresh
}

/**
 * 기기 신원 확인.
 *
 * 앱 안이면 네이티브 주입을 최대 1.5초 기다린다 — Android 는 onPageStarted 주입이
 * 실패해도 onPageFinished 에서 다시 넣기 때문. 그래도 없으면 `unavailable` 이고,
 * 그 경우 호출부는 서버로 요청을 보내지 않아야 한다.
 */
export async function resolveDeviceIdentity(): Promise<DeviceIdentity> {
  if (typeof window === 'undefined') return { source: 'unavailable', reason: 'native-id-missing' }

  if (!isNativeApp()) {
    return { source: 'browser', id: browserId() }
  }

  const deadline = Date.now() + NATIVE_WAIT_MS
  for (;;) {
    const native = readNative()
    if (native) {
      if (!isUsableId(native.id)) {
        return { source: 'unavailable', reason: 'native-id-malformed' }
      }
      return { source: 'native', id: native.id, platform: native.platform }
    }
    if (Date.now() >= deadline) break
    await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS))
  }

  // 앱 안인데 ID 가 없다. 여기서 UUID 를 만들면 예전 결함이 그대로 재발한다.
  return { source: 'unavailable', reason: 'native-id-missing' }
}

/** 사용자에게 보여줄 안내 — 임의 ID 로 우회하지 않고 상황을 말한다 */
export function unavailableMessage(reason: 'native-id-missing' | 'native-id-malformed'): string {
  return reason === 'native-id-malformed'
    ? '기기 정보를 읽지 못했습니다. 앱을 완전히 종료한 뒤 다시 열어 주세요.'
    : '기기를 확인하는 중입니다. 잠시 후 다시 시도하거나, 앱을 다시 열어 주세요.'
}

/** 기기 이름 — 관리자 화면에서 사람이 알아보기 위한 라벨일 뿐, 식별자가 아니다 */
export function getDeviceName(): string {
  if (typeof navigator === 'undefined') return ''
  const ua = navigator.userAgent
  if (/iPhone/.test(ua)) return 'iPhone'
  if (/iPad/.test(ua)) return 'iPad'
  if (/Android/.test(ua)) return ua.match(/;\s*([^;)]+)\s*Build/)?.[1]?.trim() ?? 'Android'
  if (/Macintosh/.test(ua)) return 'Mac'
  if (/Windows/.test(ua)) return 'Windows PC'
  return 'Unknown Device'
}

export function getDevicePlatform(): 'ios' | 'android' | 'web' {
  const w = nat()
  if (w.__NATIVE_PLATFORM__ === 'ios') return 'ios'
  if (w.__NATIVE_PLATFORM__ === 'android') return 'android'
  if (typeof navigator === 'undefined') return 'web'
  if (/iPhone|iPad/.test(navigator.userAgent)) return 'ios'
  if (/Android/.test(navigator.userAgent)) return 'android'
  return 'web'
}

/**
 * ⚠️ legacyDeviceId 는 보내지 않는다.
 *    자동 재매핑이 2026-07-07 에 폐기(DEVICE_REMAP_GUARD=on)됐으므로 보낼 이유가 없고,
 *    보내면 서버에 remap BLOCKED 로그만 무한히 쌓인다. 기존 Svelte 앱이 매 로드마다
 *    이걸 보내는 탓에 한 사용자가 4주간 349회 차단 로그를 남겼다.
 */
